import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { ACCOMMODATION_TYPES, ENVIRONMENT, HOTEL_FARES_PER_GROUP } from '@src/shared/src/const/app';
import useBreakpoints from '@toolkit/util/useBreakpoints';

// Actions, Models & Interfaces
import { HotelFareModel } from '@src/shared/src/models';

// Components
import { Link } from '@toolkit/ui';
import HotelRoomDetails from './HotelRoomDetails';
import { HotelFare } from './HotelFare';

// Styles
import '../styles/HotelFareGroup.scss';
import { filter } from '@src/shared/src/util/general';

type Props = {
  roomType: string;
  fares: HotelFareModel[];
  hotelRoomCount?: number;
  amenities: string[];
  env: ENVIRONMENT;
  isSelectingHotel?: boolean;
  isBasketEditable?: boolean;
  accommodationType: ACCOMMODATION_TYPES;
  onSelectHotel?: (hotelFareIds: string[]) => void;
};

export const HotelFareGroup: React.FC<Props> = ({
  roomType,
  fares,
  hotelRoomCount,
  env,
  amenities,
  isSelectingHotel,
  isBasketEditable,
  accommodationType,
  onSelectHotel,
}) => {
  const { t } = useTranslation();
  const { isMd } = useBreakpoints();
  const [expanded, setExpanded] = React.useState(false);

  const negotiatedRateFare = fares.find((fare: HotelFareModel) => fare.negotiatedRate);
  const cheapestFareNotNegotiatedRate = !fares?.[0].negotiatedRate;

  // show negotiated rate as second fare if it's not the cheapest fare
  // https://github.com/TripClickPro/product-operations/issues/1782#issuecomment-3852526670

  const otherFares = filter((fare: HotelFareModel) => fare !== negotiatedRateFare, fares);

  const orderedFares =
    negotiatedRateFare && cheapestFareNotNegotiatedRate
      ? [otherFares[0], negotiatedRateFare, ...otherFares.slice(1)]
      : [...fares];

  const showFaresCount = negotiatedRateFare ? HOTEL_FARES_PER_GROUP + 1 : HOTEL_FARES_PER_GROUP;
  const firstFare = orderedFares[0];

  const roomTypeDisplay = hotelRoomCount ? `${hotelRoomCount} x ${roomType}` : roomType;

  const visibleFares = expanded || isMd ? orderedFares : orderedFares.slice(0, showFaresCount);
  const showExpandButton = orderedFares.length > showFaresCount && !isMd;

  return (
    <div className="hotel-fare-group">
      <div className="hotel-fare-group-room">
        <div className="hotel-fare-group-room-type">
          {roomTypeDisplay}
          {negotiatedRateFare && (
            <i
              data-rh={t('hotel.tooltip.overview.negotiatedRates')}
              className="material-symbols tcp-hotel-rate-icon">
              paid
            </i>
          )}
        </div>
        <HotelRoomDetails roomDetails={firstFare.roomDetails} amenities={amenities} />
      </div>
      <div className="hotel-fare-group-fares">
        {visibleFares.map((fare) => (
          <HotelFare
            key={fare.id}
            fare={fare}
            accommodationType={accommodationType}
            env={env}
            isBasketEditable={isBasketEditable}
            onSelectHotel={onSelectHotel}
            isSelectingHotel={isSelectingHotel}
          />
        ))}
        {showExpandButton && (
          <div className="hotel-fare-group-expand">
            <Link onClick={() => setExpanded(!expanded)} className="hotel-fare-group-fares-link">
              {expanded ? t('hotelFare.button.collapse') : t('hotelFare.button.expand')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
